"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { permissionForPath } from "@/lib/permissions";
import { Sidebar } from "@/components/dashboard/sidebar";
import { Topbar } from "@/components/dashboard/topbar";
import { api, type NowPlaying } from "@/lib/api";
import { StationProvider, useStation } from "@/lib/station-context";

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  return (
    <StationProvider>
      <DashboardShell>{children}</DashboardShell>
    </StationProvider>
  );
}

function DashboardShell({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user, can } = useAuth();
  const { station, loaded } = useStation();
  const stationId = station?.id ?? null;
  const pathname = usePathname();
  const required = permissionForPath(pathname);
  const [nowPlaying, setNowPlaying] = useState<NowPlaying | null>(null);
  const [apiHealthy, setApiHealthy] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !loaded) return;

    let cancelled = false;

    async function poll() {
      try {
        const [health, playing] = await Promise.all([api.health(), api.nowPlaying()]);
        if (!cancelled) {
          setApiHealthy(health.status === "ok");
          setNowPlaying(playing);
        }
      } catch {
        if (!cancelled) setApiHealthy(false);
      }
    }

    poll();
    // 5s so the ON AIR / OFFLINE badge follows the audio closely.
    const interval = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated, loaded, stationId]);

  // AuthProvider already redirects to /login when unauthenticated — this
  // just avoids flashing dashboard content during that brief check.
  if (isLoading || !isAuthenticated || !loaded) return null;

  return (
    // Pinned to the viewport (h-screen + overflow-hidden) rather than
    // min-h-screen: with min-h-screen, any page taller than the window
    // grew the whole shell, which stretched the sidebar and pushed its
    // Now Playing card/footer off the bottom of the screen. Now the
    // sidebar and topbar stay put and only the content area below
    // scrolls; pages that want to fit exactly (the Media table) can
    // size themselves to it with h-full instead of growing the page.
    <div className="flex h-screen overflow-hidden">
      <Sidebar nowPlaying={nowPlaying} />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Topbar apiHealthy={apiHealthy} nowPlaying={nowPlaying} />
        <div className="min-h-0 flex-1 overflow-y-auto bg-bg-main p-7">
          {/* Pages that need a permission wait for the account to load, then
              show a plain "no access" note instead if the role lacks it. */}
          {required && !user ? null : required && !can(required) ? (
            <div className="mx-auto mt-16 max-w-md rounded-2xl border border-border-light bg-bg-card p-8 text-center">
              <h1 className="text-lg font-bold">You don&apos;t have access to this page</h1>
              <p className="mt-2 text-sm text-text-secondary">
                Your role doesn&apos;t include it. Ask an administrator if you need it.
              </p>
            </div>
          ) : (
            // A new key per station remounts the page, so everything on it
            // reloads for the station that was just picked.
            <div key={stationId ?? 0} className="contents">
              {station || can("stations.view") ? (
                children
              ) : (
                <div className="mx-auto mt-16 max-w-md rounded-2xl border border-border-light bg-bg-card p-8 text-center">
                  <h1 className="text-lg font-bold">No station assigned yet</h1>
                  <p className="mt-2 text-sm text-text-secondary">
                    Your account isn&apos;t linked to a station. Ask the station manager to give you access.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
